import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import apiClient from "../../business/network/ApiClient";
import { CurrentUser } from "../../business/helper/CurrentUser";

/**
 * FeatureFlagsContext — which product features this deployment currently shows.
 *
 * The back office flips a flag (Feature Flags screen) and the app hides or shows the feature on
 * the next load, no deploy. The client never decides: it renders whatever `GET /FeatureFlags`
 * returns, and the server gates the same features (404) so hiding is enough.
 *
 * A flag that is off may name test users it stays on for, so the read carries the user id and
 * the answer is this user's; the server's gates make the same per-user decision.
 *
 * <b>Unknown reads as off.</b> While the call is in flight, after it failed, or outside a
 * provider, every flag is false: a feature that appears a moment late is a non-event, a feature
 * that appears and then vanishes — or shows on a deployment that meant to keep it dark — is not.
 * A failed call is retried quietly for the life of the tab, like the agent rail.
 */
export const FEATURE_FLAGS = Object.freeze({
  INCIDENT_PREVENTION_AGENT: "incident_prevention_agent",
  SCHEDULED_EMAIL: "scheduled_email",
});

const RETRY_DELAYS = [400, 1000, 2000, 4000, 8000];
const IDLE_RETRY_MS = 60000;

const OFF = Object.freeze({});

const FeatureFlagsContext = createContext(null);

export const FeatureFlagsProvider = ({ children }) => {
  const [flags, setFlags] = useState(null); // null = not resolved yet
  const timerRef = useRef(null);
  const attemptRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearTimeout(timerRef.current);
    };
  }, []);

  const resolve = useCallback(async () => {
    // The user the app is showing: a flag that is off for everyone may still be on for them
    // (the back office's test users), so the server needs to know who is asking.
    const result = await apiClient.getFeatureFlags(CurrentUser.GetCurrentUserData()?.userId);
    if (!isMountedRef.current) return;

    if (result === null) {
      attemptRef.current += 1;
      const inBackoff = attemptRef.current <= RETRY_DELAYS.length;
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(
        resolve,
        inBackoff ? RETRY_DELAYS[attemptRef.current - 1] : IDLE_RETRY_MS
      );
      return;
    }

    attemptRef.current = 0;
    setFlags(result);
  }, []);

  useEffect(() => {
    resolve();
  }, [resolve]);

  const value = useMemo(
    () => ({
      flags: flags ?? OFF,
      isResolved: flags !== null,
      isEnabled: (key) => !!(flags ?? OFF)[key],
      refresh: resolve,
    }),
    [flags, resolve]
  );

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
};

const OUTSIDE_PROVIDER = Object.freeze({
  flags: OFF,
  isResolved: false,
  isEnabled: () => false,
  refresh: () => Promise.resolve(),
});

/** Safe anywhere: outside a provider every flag reads as off. */
export const useFeatureFlags = () => useContext(FeatureFlagsContext) ?? OUTSIDE_PROVIDER;

/** `true` only once the server said so. */
export const useFeatureFlag = (key) => useFeatureFlags().isEnabled(key);

export default FeatureFlagsContext;
